import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { openModal } from "react-url-modal";
import { TopBar } from "@/components";
import { TopBarItem } from "@/components/TopBar/types";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  fetchGetAvailableTournaments,
  fetchGetMyApplicationsTournaments,
  fetchGetCreatedTournaments,
  fetchGetTournamentApplicationsCount,
  fetchGetUserTournamentStatus,
  fetchChangeUserTournamentStatus,
  setUserTournamentStatus,
  setTournamentsLoading,
  selectAvailableTournaments,
  selectMyApplicationsTournaments,
  selectMyCreatedTournaments,
  selectTournamentApplicationsCount,
  selectTournamentsLoading,
  selectUserTournamentStatus,
  TOURNAMENT_USER_TYPES,
} from "@/redux/tournaments";
import { getTokenFromStorage } from "@/utils";
import { useCurrentWidth } from "@/hooks";
import { MediaBreakPoints } from "@/resources/constants";
import { AvailableTournamentsScreen } from "../components/AvailableTournamentsScreen/index";
import { MyTournamentsScreen } from "../components/MyTournamentsScreen/index";
import { ChangeStatusModal } from "../components/ChangeStatusModal/index";
import { getProfileInfo } from "@/redux/profile/settings/getProfileInfo/selectors";
import styles from "./Tournaments.module.scss";
import arrowIcon from "@/assets/icons/arrowCalendarRightGreen.svg";

export const Tournaments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const windowWidth = useCurrentWidth();
  const breakPoint = MediaBreakPoints.MOBILE_LARGE;

  const availableTournaments = useAppSelector(selectAvailableTournaments);
  const myApplicationsTournaments = useAppSelector(selectMyApplicationsTournaments);
  const myCreatedTournaments = useAppSelector(selectMyCreatedTournaments);
  const applicationsCount = useAppSelector(selectTournamentApplicationsCount);
  const isLoading = useAppSelector(selectTournamentsLoading);
  const userTournamentStatus = useAppSelector(selectUserTournamentStatus);
  const { profileInfo } = useAppSelector(getProfileInfo);

  const [activeTab, setActiveTab] = useState<"available" | "my">("available");
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [requestStatus, setRequestStatus] = useState<string | null>(null);
  const [modalVisibleContent, setModalVisibleContent] = useState(0);

  const statusFromProfile = profileInfo?.role;
  const finalStatus = userTournamentStatus || statusFromProfile;

  const isParticipant =
    finalStatus === TOURNAMENT_USER_TYPES.PARTICIPANT || finalStatus === "USER";
  const isOrganizer =
    finalStatus === TOURNAMENT_USER_TYPES.ORGANIZER || finalStatus === "ORGANIZATOR";
  const token = getTokenFromStorage();

  const topBarItems: TopBarItem[] = React.useMemo(
    () => [
      {
        id: "available",
        text: "Доступные турниры",
        path: "available",
      },
      {
        id: "my",
        text: "Мои турниры",
        path: "my",
        badge: applicationsCount > 0 ? applicationsCount : undefined,
      },
    ],
    [applicationsCount]
  );

  useEffect(() => {
    dispatch(setTournamentsLoading(true));
    loadTournaments();
    loadUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profileInfo?.role && !userTournamentStatus) {
      dispatch(setUserTournamentStatus(profileInfo.role));
    }
  }, [profileInfo?.role, userTournamentStatus, dispatch]);

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    const token = getTokenFromStorage();
    if (token && (path === "my" || path === "available")) {
      loadTournaments();
    }
  }, [location.pathname]);

  const loadUserStatus = async () => {
    const token = getTokenFromStorage();
    if (token) {
      try {
        const status = await dispatch(fetchGetUserTournamentStatus()).unwrap();
        setRequestStatus(status);

        if (profileInfo?.role && !status) {
          dispatch(setUserTournamentStatus(profileInfo.role));
        } else if (status) {
          dispatch(setUserTournamentStatus(status));
        }
      } catch (error) {
        console.error("Error loading user status:", error);
        if (profileInfo?.role) {
          dispatch(setUserTournamentStatus(profileInfo.role));
        }
      }
    }
  };

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    if (path === "my") {
      setActiveTab("my");
    } else if (path === "available") {
      setActiveTab("available");
    } else if (path === "applied") {
      // Redirect old "applied" path to "my"
      navigate("/padel/tournaments/my", { replace: true });
    } else if (/^\d+$/.test(path || '')) {
      // Deeplink по ID турнира — открываем модалку
      setActiveTab("available");
      openModal({ name: "joinTournament", params: { idTournament: path!, fromDeeplink: "true" } });
    } else if (
      path === "tournaments" ||
      location.pathname === "/padel/tournaments"
    ) {
      setActiveTab("available");
      if (windowWidth > breakPoint) {
        navigate("/padel/tournaments/available", { replace: true });
      }
    }
  }, [location.pathname, navigate, windowWidth, breakPoint]);

  const loadTournaments = async () => {
    const token = getTokenFromStorage();
    try {
      dispatch(setTournamentsLoading(true));

      if (token) {
        await Promise.all([
          dispatch(fetchGetAvailableTournaments()),
          dispatch(fetchGetMyApplicationsTournaments()),
          isOrganizer
            ? dispatch(fetchGetCreatedTournaments())
            : Promise.resolve(),
          isOrganizer
            ? dispatch(fetchGetTournamentApplicationsCount())
            : Promise.resolve(),
        ]);
      } else {
        await dispatch(fetchGetAvailableTournaments());
      }

      dispatch(setTournamentsLoading(false));
    } catch (error) {
      console.error("Error loading tournaments:", error);
      dispatch(setTournamentsLoading(false));
    }
  };

  const handleTabChange = (item: TopBarItem) => {
    const token = getTokenFromStorage();
    if (item.id === "my" && !token) {
      setIsAuthModalOpen(true);
      return;
    }
    navigate(`/padel/tournaments/${item.id}`);
  };

  const handleBecomeOrganizer = () => {
    setIsChangeStatusModalOpen(true);
  };

  const handleConfirmChangeStatus = async () => {
    setIsChangingStatus(true);
    try {
      const newStatus = await dispatch(
        fetchChangeUserTournamentStatus({ role: "ORGANIZATOR" })
      ).unwrap();

      if (newStatus) {
        setRequestStatus(newStatus);
        setModalVisibleContent(1);
        await loadTournaments();
      }
    } catch (error) {
      console.error("Error changing status:", error);
    } finally {
      setIsChangingStatus(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "available":
        return (
          <AvailableTournamentsScreen
            listTournaments={availableTournaments}
            setIsRequiredAuthOpened={setIsAuthModalOpen}
          />
        );
      case "my":
        return (
          <MyTournamentsScreen
            isOrganizer={isOrganizer}
            createdTournaments={myCreatedTournaments}
            appliedTournaments={myApplicationsTournaments}
            applicationsCount={applicationsCount}
            setIsRequiredAuthOpened={setIsAuthModalOpen}
          />
        );
      default:
        return (
          <AvailableTournamentsScreen
            listTournaments={availableTournaments}
            setIsRequiredAuthOpened={setIsAuthModalOpen}
          />
        );
    }
  };

  return (
    <>
      {getTokenFromStorage() && (
        <div className={styles.statusSection}>
          <span className={styles.statusText}>
            Ваш статус:{" "}
            {isOrganizer && (
              <span className={styles.statusBadge}>Организатор</span>
            )}
            {isParticipant && !isOrganizer && (
              <span className={styles.statusBadge}>Участник</span>
            )}
          </span>

          {isParticipant && requestStatus !== "PENDING" && (
            <button
              onClick={handleBecomeOrganizer}
              className={styles.becomeOrganizerButton}
            >
              Стать организатором
              <img src={arrowIcon} alt="arrow icon" />
            </button>
          )}
          {isParticipant && requestStatus === "PENDING" && (
            <div className={styles.pendingText}>Запрос отправлен</div>
          )}
        </div>
      )}

      {windowWidth > breakPoint ? (
        <div className={styles.wrapper}>
          <TopBar
            items={topBarItems}
            basePath="/padel/tournaments"
            onItemClick={handleTabChange}
            breakPoint={breakPoint}
            alwaysTabs
          />
          <div className={styles.content}>{renderContent()}</div>
        </div>
      ) : (
        <div className={styles.mobileWrapper}>
          <TopBar
            items={topBarItems}
            basePath="/padel/tournaments"
            onItemClick={handleTabChange}
            breakPoint={breakPoint}
            alwaysTabs
          />
          <div className={styles.mobileContent}>{renderContent()}</div>
        </div>
      )}

      <ChangeStatusModal
        isOpen={isChangeStatusModalOpen}
        setIsOpen={setIsChangeStatusModalOpen}
        onConfirm={handleConfirmChangeStatus}
        isLoading={isChangingStatus}
        modalVisibleContent={modalVisibleContent}
        setModalVisibleContent={setModalVisibleContent}
      />
    </>
  );
};
